import { ChangeEvent, useMemo } from "react";
import { useNavigate } from "react-router";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { PencilIcon } from "@heroicons/react/24/outline";
import { localeShortDateTime } from "../../app/datetimeUtils";
import { isHigherThanDistrictAdmin } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import { CourseListViewModel } from "./schoolsApiSlice";

interface CourseListTableModel extends CourseListViewModel {
	isRowSelected: boolean;
}

interface SchoolCoursesTableProps {
	courses: CourseListViewModel[];
	selectedCourses: CourseListViewModel[];
	setSelectedCourses: (
		courses: CourseListViewModel[] | ((prev: CourseListViewModel[]) => CourseListViewModel[])
	) => void;
}

export default function SchoolCoursesTable(props: SchoolCoursesTableProps) {
	const { config } = useAppSelector((state) => state.site);
	const { role } = useAppSelector((state) => state.user);

	const navigate = useNavigate();

	const hasClassLinkOrCleverCourse = props.courses.some(
		(c) => c.isClassLinkCourse || c.isCleverCourse
	);

	const checkboxBodyTemplate = (row: CourseListTableModel) => {
		return (
			<input
				type="checkbox"
				value={row.courseId}
				checked={row.isRowSelected}
				onChange={handleSelectOne}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select course: ${row.name}`}
			/>
		);
	};

	const createDateBodyTemplate = (row: CourseListTableModel) => {
		return <span>{localeShortDateTime(row.createDate)}</span>;
	};

	const cleverOrClassLinkBodyTemplate = (row: CourseListTableModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={row.isClassLinkCourse}
				isCleverEntity={row.isCleverCourse}
			/>
		);
	};

	const editBodyTemplate = (row: CourseListTableModel) => {
		return (
			<button
				id={`edit-${row.courseId}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => navigate(`/schools/course?courseId=${row.courseId}`)}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	const handleSelectOne = (e: ChangeEvent<HTMLInputElement>) => {
		const courseId = Number(e.currentTarget.value);
		const isChecked = e.currentTarget.checked;
		const course = props.courses.find((c) => c.courseId === courseId);
		if (!course) return;
		if (isChecked) {
			props.setSelectedCourses((prev) => [...prev, course]);
		} else {
			props.setSelectedCourses((prev) => prev.filter((c) => c.courseId !== courseId));
		}
	};

	// Handles on a per page basis
	const handleSelectAll = (isChecked: boolean) => {
		if (isChecked) {
			// Add any missing courses from current page to selectedCourses
			props.setSelectedCourses((prev) => {
				const newCourses = props.courses.filter(
					(c) => !prev.some((sc) => sc.courseId === c.courseId)
				);
				return [...prev, ...newCourses];
			});
		} else {
			// Remove all courses from current page from selectedCourses
			props.setSelectedCourses((prev) =>
				prev.filter((sc) => !props.courses.some((c) => c.courseId === sc.courseId))
			);
		}
	};

	const allChecked = useMemo(() => {
		// returns if all courses on the page are in the selectedCourses
		return (
			props.courses.length > 0 &&
			props.courses.every((c) =>
				props.selectedCourses.some((sc) => sc.courseId === c.courseId)
			)
		);
	}, [props.courses, props.selectedCourses]);

	const coursesWithSelection: CourseListTableModel[] = useMemo(() => {
		return props.courses.map((course) => ({
			...course,
			isRowSelected: props.selectedCourses.some((c) => c.courseId === course.courseId),
		}));
	}, [props.courses, props.selectedCourses]);

	return (
		<DataTable
			value={coursesWithSelection}
			dataKey="courseId"
			emptyMessage="No courses found"
			stripedRows
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ marginTop: "16px" }}>
			{isHigherThanDistrictAdmin(role!) && (
				<Column
					columnKey="course-checkbox"
					header={
						<input
							type="checkbox"
							checked={allChecked}
							onChange={(e) => handleSelectAll(e.target.checked)}
							className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
							aria-label="Select all courses on page"
						/>
					}
					body={checkboxBodyTemplate}
					className="w-12"
					headerClassName="text-center"
					bodyClassName="text-center"
				/>
			)}
			<Column header="Created" body={createDateBodyTemplate}></Column>
			{!config?.isUtahComposeSite && hasClassLinkOrCleverCourse && (
				<Column
					body={cleverOrClassLinkBodyTemplate}
					headerClassName="!px-0 w-4"
					className="!px-0"></Column>
			)}
			<Column header="Name" field="name"></Column>
			<Column header="External ID" field="externalID"></Column>
			<Column header="Referral Code" field="referralCode"></Column>
			<Column header="Status" field="status"></Column>
			<Column header="Grade" field="grade"></Column>
			<Column header="Students" field="writerCount"></Column>
			<Column body={editBodyTemplate} className="!pl-1 !pr-2"></Column>
		</DataTable>
	);
}
